import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import api from "./api";

const LocationContext = createContext(null);

const STORAGE_KEY = "dh_city";

async function ipLookup() {
  try {
    // Server-side proxy to avoid ipapi.co CORS restrictions
    const r = await api.get("/geo/ip");
    return r.data?.city || null;
  } catch (e) {
    return null;
  }
}

async function browserGeo() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          // Reverse-geocode via BigDataCloud free tier (no key)
          const r = await axios.get(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
            { timeout: 4000 }
          );
          resolve(r.data?.city || r.data?.locality || null);
        } catch (e) {
          resolve(null);
        }
      },
      () => resolve(null),
      { timeout: 8000, maximumAge: 3600 * 1000 }
    );
  });
}

export function LocationProvider({ children }) {
  const [city, setCity] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [supported, setSupported] = useState([]);
  const [detecting, setDetecting] = useState(false);

  const setCityPersist = useCallback((val) => {
    setCity(val || "");
    if (val) localStorage.setItem(STORAGE_KEY, val);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const nearestSupported = useCallback((detected, list) => {
    if (!detected || !list || list.length === 0) return null;
    const lower = detected.toLowerCase();
    const exact = list.find((c) => c.toLowerCase() === lower);
    if (exact) return exact;
    // rough country hint mapping via detected value (very best-effort)
    return null;
  }, []);

  const detect = useCallback(async () => {
    setDetecting(true);
    try {
      const geo = await browserGeo();
      const list = supported.length ? supported : (await api.get("/locations").catch(() => ({ data: [] }))).data;
      const mapped = nearestSupported(geo, list);
      if (mapped) { setCityPersist(mapped); return mapped; }
      const ip = await ipLookup();
      const mappedIp = nearestSupported(ip, list);
      if (mappedIp) { setCityPersist(mappedIp); return mappedIp; }
      return null;
    } finally {
      setDetecting(false);
    }
  }, [supported, setCityPersist, nearestSupported]);

  const requestGeo = useCallback(async () => {
    setDetecting(true);
    try {
      const geo = await browserGeo();
      const list = supported.length ? supported : (await api.get("/locations").catch(() => ({ data: [] }))).data;
      const mapped = nearestSupported(geo, list);
      if (mapped) setCityPersist(mapped);
      return mapped;
    } finally {
      setDetecting(false);
    }
  }, [supported, setCityPersist, nearestSupported]);

  useEffect(() => {
    api.get("/locations").then((r) => {
      const list = r.data || [];
      setSupported(list);
      // Silent IP-based detect only if no stored city; only persist if it maps to a supported city
      if (!city) {
        ipLookup().then((c) => {
          const mapped = nearestSupported(c, list);
          if (mapped) setCityPersist(mapped);
        });
      }
    }).catch(() => {});
  }, []);

  return (
    <LocationContext.Provider value={useMemo(
      () => ({ city, setCity: setCityPersist, supported, detect, requestGeo, detecting }),
      [city, setCityPersist, supported, detect, requestGeo, detecting],
    )}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  return useContext(LocationContext);
}
